/**
 * AiDraftingBanner — Order Review page banner showing live AI-drafting
 * activity, so CSRs don't create manual order shells while the AI is mid-
 * classification (drafts appear on their own within ~2 minutes).
 *
 * Self-polls GET /portal/inbox/classification-activity. When the drafting
 * count drops between polls, new drafts have likely landed — the banner
 * fires `onActivitySettled` so the parent can refresh the orders list.
 */
import { useEffect, useRef, useState } from 'react';
import { portalService } from '../../services/portal';
import type { PortalClassificationActivity } from '../../types/portal';
import {
  AI_DRAFT_POLL_MS,
  classificationActivityBanner,
} from '../../utils/aiDrafting';
import styles from './AiDraftingBanner.module.css';

interface AiDraftingBannerProps {
  /** Called when the drafting count decreases (new drafts likely landed). */
  onActivitySettled?: () => void;
}

const AiDraftingBanner: React.FC<AiDraftingBannerProps> = ({
  onActivitySettled,
}) => {
  const [activity, setActivity] =
    useState<PortalClassificationActivity | null>(null);
  const prevDraftingRef = useRef<number | null>(null);
  const settledCbRef = useRef(onActivitySettled);
  settledCbRef.current = onActivitySettled;

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await portalService.getClassificationActivity();
        if (cancelled) return;
        const prev = prevDraftingRef.current;
        prevDraftingRef.current = data.drafting;
        setActivity(data);
        if (prev !== null && data.drafting < prev) {
          settledCbRef.current?.();
        }
      } catch {
        // Banner is best-effort — never break the Orders page over it.
      }
    };

    poll();
    const intervalId = window.setInterval(poll, AI_DRAFT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const banner = classificationActivityBanner(activity);
  if (!banner) return null;

  return (
    <div
      className={banner.kind === 'failed' ? styles.failed : styles.drafting}
      role={banner.kind === 'failed' ? 'alert' : 'status'}
      data-testid="ai-drafting-banner"
    >
      {banner.kind === 'drafting' && (
        <span className={styles.spinner} aria-hidden="true" />
      )}
      {banner.kind === 'failed' && (
        <span className={styles.warnIcon} aria-hidden="true">&#9888;</span>
      )}
      <span>{banner.text}</span>
    </div>
  );
};

export default AiDraftingBanner;
